/**
 * Per-chat turn history of the Graph view, pure and storage-agnostic (useDiagHistory wires it to localStorage).
 *
 * The backend only sends the latest tse:diag_state, so every turn is kept client-side: in memory for this page, and in ONE
 * localStorage record (TURN_STORE_KEY) so the breakdown is still there when the engineer comes back to the chat. Stored per chat:
 * the compact diag of each turn (only what lightsFromState, the sidebar and the breakdown read), MAX_TURNS newest turns, the
 * MAX_CHATS most recently saved chats, dropped after TTL_MS. A full store drops the oldest chats, then the oldest turns.
 *
 * Entry shape: {seq, turnNo, answers: [{question, answer}], diag}.
 */
export const TURN_STORE_KEY = "d5.lights.turns.v1";
export const MAX_TURNS = 60;
export const MAX_CHATS = 20;
export const TTL_MS = 14 * 24 * 60 * 60 * 1000;
const QUOTA_RETRY_TURN_DIVISOR = 2;

const list = (v) => (Array.isArray(v) ? v : []);
const text = (v) => (typeof v === "string" ? v : "");

/** The slice of a diag_state the Graph view needs for a past turn (lights, status card, node and edge details, breakdown). */
export function compactDiag(diag) {
  if (!diag || typeof diag !== "object") return null;
  return {
    session_id: diag.session_id, diagnosis_id: diag.diagnosis_id, seq: diag.seq, category: diag.category,
    phase: diag.phase ?? null, lifecycle: diag.lifecycle ?? null, last: diag.last ?? null,
    step: diag.step?.step_id ? { step_id: diag.step.step_id } : null,
    causes: list(diag.causes).map((c) => ({
      key: c.key, name: c.name, percentage: c.percentage, status: c.status, lod: c.lod ?? null, reason: c.reason ?? null, fix: c.fix ?? null,
      clues: list(c.clues).map((k) => ({ fact_id: k.fact_id, label: k.label })),
    })),
    ruled_out: list(diag.ruled_out).map((r) => ({ cause_key: r.cause_key, reason: r.reason, by_fact: r.by_fact ?? null })),
    observed: list(diag.observed).map((o) => ({ id: o.id, label: o.label, value: o.value, source: o.source ?? null })),
    lods: list(diag.lods).map((l) => ({ id: l.id, label: l.label, status: l.status, mass: l.mass })),
  };
}

const sameLast = (a, b) => !!a && !!b && a.question_id === b.question_id && a.answer === b.answer;

/**
 * The questions answered in this turn: the newest `turns[]` entry when the DA turn count moved on, else the `last` answer when
 * it changed (its question text from `asked`), else nothing (a command bumped seq without an answer).
 */
function answersOf(diag, prev) {
  const turns = list(diag.turns);
  const newest = turns[turns.length - 1];
  const prevTurnNo = prev?.turnNo ?? null;
  if (newest && Number.isInteger(newest.turn) && (prevTurnNo == null || newest.turn > prevTurnNo)) {
    return { turnNo: newest.turn, answers: list(newest.answers).map((a) => ({ question: text(a.question), answer: text(a.answer) })) };
  }
  if (diag.last?.answer && !sameLast(diag.last, prev?.diag?.last)) {
    const asked = list(diag.asked).find((a) => a.question_id === diag.last.question_id);
    return { turnNo: prevTurnNo, answers: [{ question: text(asked?.text), answer: text(diag.last.answer) }] };
  }
  return { turnNo: prevTurnNo, answers: [] };
}

/**
 * `history` with `diag` merged in by seq: a new diagnosis_id starts over, an existing seq is replaced, otherwise inserted in
 * order (never duplicated). The diag itself is kept as given (full while live, compact once hydrated).
 */
export function mergeTurn(history, diag) {
  const base = list(history);
  const tail = base[base.length - 1];
  const turns = tail && tail.diag?.diagnosis_id !== diag.diagnosis_id ? [] : base;
  const seq = Number.isInteger(diag.seq) && diag.seq > 0 ? diag.seq : (turns.length ? turns[turns.length - 1].seq + 1 : 1);
  const at = turns.findIndex((t) => t.seq >= seq);
  const prev = at === -1 ? turns[turns.length - 1] : turns[at - 1];
  const entry = { seq, ...answersOf(diag, prev), diag };
  let next;
  if (at === -1) next = [...turns, entry];
  else if (turns[at].seq === seq) next = [...turns.slice(0, at), entry, ...turns.slice(at + 1)];
  else next = [...turns.slice(0, at), entry, ...turns.slice(at)];
  return next.slice(-MAX_TURNS);
}

const isEntry = (t) => !!t && Number.isInteger(t.seq) && t.seq > 0 && !!t.diag && typeof t.diag === "object" && typeof t.diag.diagnosis_id === "string";

/** The parsed store, {chats: {}} when absent or unreadable. */
function readRoot(storage) {
  try {
    const raw = storage.getItem(TURN_STORE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const chats = parsed && typeof parsed === "object" && parsed.chats && typeof parsed.chats === "object" ? parsed.chats : {};
    return { chats };
  } catch (_) {
    return { chats: {} };
  }
}

const fresh = (chat, now) => !!chat && typeof chat.savedAt === "number" && now - chat.savedAt <= TTL_MS;

/** The stored turns of `chatId` (oldest first), [] when none, expired or unreadable. Never throws. */
export function loadChat(storage, chatId, now = Date.now()) {
  if (!storage || !chatId) return [];
  const chat = readRoot(storage).chats[chatId];
  if (!fresh(chat, now)) return [];
  const turns = list(chat.turns).filter(isEntry)
    .map((t) => ({ seq: t.seq, turnNo: Number.isInteger(t.turnNo) ? t.turnNo : null, answers: list(t.answers), diag: t.diag }));
  const diagnosisId = turns[turns.length - 1]?.diag.diagnosis_id;
  return turns.filter((t) => t.diag.diagnosis_id === diagnosisId).sort((a, b) => a.seq - b.seq).slice(-MAX_TURNS);
}

function writeRoot(storage, root) {
  try {
    storage.setItem(TURN_STORE_KEY, JSON.stringify(root));
    return true;
  } catch (_) {
    return false;
  }
}

const oldestChatId = (chats, except) => Object.keys(chats).filter((id) => id !== except)
  .sort((a, b) => (chats[a].savedAt || 0) - (chats[b].savedAt || 0))[0];

/**
 * Saves `turns` as the history of `chatId`: expired chats pruned, the MAX_CHATS most recent kept. On a failed write (quota)
 * the oldest other chats are dropped first, then this chat's oldest turns, until it fits or nothing is left. Never throws.
 */
export function saveChat(storage, chatId, turns, now = Date.now()) {
  if (!storage || !chatId) return false;
  const root = readRoot(storage);
  const chats = {};
  Object.keys(root.chats).forEach((id) => { if (id !== chatId && fresh(root.chats[id], now)) chats[id] = root.chats[id]; });
  Object.keys(chats).sort((a, b) => (chats[b].savedAt || 0) - (chats[a].savedAt || 0)).slice(MAX_CHATS - 1).forEach((id) => { delete chats[id]; });
  let own = list(turns).slice(-MAX_TURNS).map((t) => ({ seq: t.seq, turnNo: t.turnNo ?? null, answers: list(t.answers), diag: compactDiag(t.diag) }));
  for (;;) {
    chats[chatId] = { savedAt: now, turns: own };
    if (writeRoot(storage, { chats })) return true;
    const victim = oldestChatId(chats, chatId);
    if (victim) { delete chats[victim]; continue; }
    if (own.length <= 1) return false;
    own = own.slice(Math.ceil(own.length / QUOTA_RETRY_TURN_DIVISOR));
  }
}

/** window.localStorage when it can be used, else null (private mode, blocked storage, no window). */
export function defaultStorage() {
  try {
    const s = typeof window !== "undefined" ? window.localStorage : null;
    if (!s) return null;
    s.getItem(TURN_STORE_KEY);
    return s;
  } catch (_) {
    return null;
  }
}
